import { migrateProjectDocument } from '../project/migrateProjectDocument.js'
import type { ProjectDocumentV2 } from '../project/ProjectDocument.js'

export interface ProjectDocumentSource {
  getProject(): unknown
  subscribe(listener: () => void): () => void
}

export interface ProjectAutosaveOptions {
  readonly schedule?: (callback: () => void) => () => void
  readonly onError?: (error: unknown) => void
  readonly onSaved?: () => void
}

export interface ProjectAutosaveController {
  getLastError(): unknown
  flush(): Promise<void>
  dispose(): Promise<void>
}

interface PendingSave {
  readonly revision: number
  readonly document: ProjectDocumentV2
}

function defaultSchedule(callback: () => void): () => void {
  const handle = globalThis.setTimeout(callback, 500)
  return () => { globalThis.clearTimeout(handle) }
}

/**
 * Subscribes to document identity only. Transient viewport/playback root
 * publications are ignored, while rapid document edits collapse into one
 * synchronously snapshotted ProjectRepository write.
 */
export function createProjectAutosaveController(
  source: ProjectDocumentSource,
  save: (document: ProjectDocumentV2) => Promise<void>,
  options: ProjectAutosaveOptions = {},
): ProjectAutosaveController {
  const schedule = options.schedule ?? defaultSchedule
  let observed: unknown = source.getProject()
  let revision = 0
  let pending: PendingSave | null = null
  let retry: PendingSave | null = null
  let cancelScheduled: (() => void) | null = null
  let saveTail: Promise<void> = Promise.resolve()
  let lastError: unknown = null
  let disposed = false

  const report = (error: unknown) => {
    try { options.onError?.(error) } catch { /* Host error observers are contained. */ }
  }

  const enqueuePending = (): Promise<void> => {
    const candidate = pending ?? retry
    if (candidate === null) return saveTail
    if (pending === candidate) pending = null
    if (retry === candidate) retry = null
    const operation = saveTail.then(async () => {
      try {
        await save(candidate.document)
        if (candidate.revision === revision) {
          retry = null
          lastError = null
        }
        try { options.onSaved?.() } catch { /* Host save observers are contained. */ }
      } catch (error) {
        lastError = error
        if (candidate.revision === revision && pending === null) retry = candidate
        report(error)
      }
    })
    saveTail = operation
    return saveTail
  }

  const arm = () => {
    if (cancelScheduled !== null || disposed) return
    let firedSynchronously = false
    const cancel = schedule(() => {
      firedSynchronously = true
      cancelScheduled = null
      void enqueuePending()
    })
    if (!firedSynchronously) cancelScheduled = cancel
  }

  const publish = () => {
    if (disposed) return
    let document: unknown
    try {
      document = source.getProject()
    } catch (error) {
      report(error)
      return
    }
    if (document === observed) return
    observed = document
    try {
      revision += 1
      pending = { revision, document: migrateProjectDocument(document) }
      retry = null
    } catch (error) {
      report(error)
      return
    }
    arm()
  }

  const unsubscribe = source.subscribe(publish)

  const flush = async (): Promise<void> => {
    cancelScheduled?.()
    cancelScheduled = null
    do {
      await enqueuePending()
      await saveTail
    } while (pending !== null)
  }

  return {
    getLastError: () => lastError,
    flush,
    async dispose() {
      if (disposed) return
      disposed = true
      try { unsubscribe() } catch (error) { report(error) }
      await flush()
    },
  }
}
